<template>
  <div>
    <!-- Hero Section -->
    <section class="gradient-navy section-decorated min-h-[90vh] flex items-center relative">
      <!-- Grid background -->
      <div class="absolute inset-0 grid-bg opacity-50"></div>
      <!-- Decorative orbs -->
      <div class="absolute top-20 right-10 w-72 h-72 rounded-full glow-orb-10 animate-float"></div>
      <div class="absolute bottom-20 left-10 w-96 h-96 rounded-full glow-orb-5 animate-float-delayed"></div>
      <div class="absolute top-1/2 left-1/2 w-[500px] h-[500px] rounded-full glow-orb-5 -translate-x-1/2 -translate-y-1/2"></div>

      <div class="container mx-auto px-4 py-32 relative z-10">
        <div class="grid lg:grid-cols-2 gap-12 items-center">
        <div class="max-w-3xl">
          <h1 class="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
            {{ t.home.hero.titleLine1 }}
            <br />
            {{ t.home.hero.titlePre }}<span class="text-gradient">{{ t.home.hero.titleHighlight }}</span>{{ t.home.hero.titlePost }}
          </h1>
          <p class="text-lg sm:text-xl text-white/70 leading-relaxed max-w-2xl mb-10">
            {{ t.home.hero.paragraph }}
          </p>
          <div class="flex flex-wrap gap-4">
            <router-link to="/portfolio" class="btn-primary">
              {{ t.home.hero.viewWork }}
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </router-link>
            <router-link to="/contact" class="btn-outline border-white/20 text-white hover:bg-white hover:text-td-navy">
              {{ t.home.hero.contactUs }}
            </router-link>
          </div>
        </div>

        <HeroVisual class="hidden lg:block" />
        </div>
      </div>

      <!-- Wave divider -->
      <div class="wave-divider -bottom-px">
        <svg viewBox="0 0 1440 80" preserveAspectRatio="none" class="block w-full h-12 md:h-20 -mb-px">
          <path d="M0,40 C360,80 720,0 1080,50 C1260,70 1380,30 1440,40 L1440,80 L0,80 Z" fill="#ffffff"/>
        </svg>
      </div>
    </section>

    <!-- Services Section -->
    <section class="bg-white section-decorated py-12 md:py-16">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <p class="text-td-blue font-semibold text-sm uppercase tracking-wider mb-3">{{ t.home.services.kicker }}</p>
          <h2 class="text-3xl md:text-4xl font-extrabold text-td-navy mb-4">{{ t.home.services.title }}</h2>
          <div class="w-16 h-1 bg-td-blue rounded-full mx-auto"></div>
        </div>

        <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div v-for="service in services" :key="service.title" class="group p-6 rounded-2xl border border-td-light hover:border-td-blue/20 hover:shadow-xl hover:shadow-td-blue/5 transition-all duration-300 hover:-translate-y-1">
            <div class="w-12 h-12 rounded-xl bg-td-blue/10 flex items-center justify-center mb-5 group-hover:bg-td-blue/20 transition-colors">
              <div class="text-td-blue" v-html="service.icon"></div>
            </div>
            <h3 class="text-lg font-bold text-td-navy mb-2">{{ service.title }}</h3>
            <p class="text-sm text-td-gray leading-relaxed">{{ service.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Stats Section -->
    <section class="gradient-navy section-decorated py-12">
      <!-- Grid bg -->
      <div class="absolute inset-0 grid-bg opacity-30"></div>
      <div class="absolute top-0 left-1/4 w-64 h-64 rounded-full glow-orb-10"></div>

      <div class="container mx-auto px-4 relative z-10">
        <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div v-for="stat in t.home.stats" :key="stat.label" class="text-center">
            <p class="text-4xl md:text-5xl font-extrabold text-white mb-2">{{ stat.value }}</p>
            <p class="text-td-blue-light text-sm font-semibold uppercase tracking-wider">{{ stat.label }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Industries Section -->
    <section class="bg-td-lighter section-decorated py-12 md:py-16">
      <div class="container mx-auto px-4">
        <div class="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p class="text-td-blue font-semibold text-sm uppercase tracking-wider mb-3">{{ t.home.industries.kicker }}</p>
            <h2 class="text-3xl md:text-4xl font-extrabold text-td-navy mb-6">{{ t.home.industries.title }}</h2>
            <p class="text-td-gray leading-relaxed mb-6">
              {{ t.home.industries.paragraph }}
            </p>
            <div class="space-y-4">
              <div v-for="industry in t.home.industries.items" :key="industry.title" class="flex items-start gap-4">
                <div class="w-8 h-8 rounded-lg bg-td-blue/10 flex items-center justify-center shrink-0 mt-0.5">
                  <svg class="w-4 h-4 text-td-blue" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p class="font-bold text-td-navy">{{ industry.title }}</p>
                  <p class="text-sm text-td-gray">{{ industry.description }}</p>
                </div>
              </div>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-4">
            <div v-for="tech in t.home.industries.technologies" :key="tech" class="card-glass p-5 text-center">
              <p class="font-bold text-td-navy text-sm">{{ tech }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="bg-white section-decorated py-12 md:py-16">
      <div class="container mx-auto px-4">
        <div class="gradient-navy rounded-3xl p-10 md:p-16 text-center relative overflow-hidden">
          <div class="absolute inset-0 grid-bg opacity-20"></div>
          <div class="absolute top-0 right-0 w-64 h-64 rounded-full glow-orb-10"></div>
          <div class="absolute bottom-0 left-0 w-48 h-48 rounded-full glow-orb-10"></div>
          <div class="relative z-10">
            <h2 class="text-3xl md:text-4xl font-extrabold text-white mb-4">{{ t.home.cta.title }}</h2>
            <p class="text-white/60 max-w-xl mx-auto mb-8">
              {{ t.home.cta.paragraph }}
            </p>
            <router-link to="/contact" class="btn-primary text-base px-10 py-4">
              {{ t.home.cta.button }}
              <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </router-link>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import HeroVisual from "@/components/HeroVisual.vue";
import { useI18n } from "@/i18n";

const { t } = useI18n();

const serviceIcons = [
  '<svg class="w-6 h-6" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" /></svg>',
  '<svg class="w-6 h-6" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" /></svg>',
  '<svg class="w-6 h-6" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>',
  '<svg class="w-6 h-6" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" /></svg>',
];

const services = computed(() =>
  t.value.home.services.items.map((item, i) => ({ ...item, icon: serviceIcons[i] }))
);
</script>
